import { useCallback, useState } from "react";

/**
 * useCompareStream
 *
 * Treibt die fünf Compare-Phasen (Auth, Quelle, Ziel, Refs, Diff) im
 * Wizard-Schritt 1 visuell vor sich her, damit der Nutzer einen Fortschritt
 * sieht während das Backend die Operationen sequenziell ausführt.
 *
 * Vor jedem await wird die Phase auf "active" gesetzt, danach auf "done"
 * (oder "error", falls der Step wirft). Die Dauer wird in Sekunden geloggt,
 * damit das UI sie neben der Phase rendern kann.


 *
 * startCompare(api, params)
 *   - api:    die helpers.api()-Funktion (oder ein kompatibler fetch-Wrapper)
 *   - params: {
 *       sourceInstanceId, targetInstanceId, sourcePackageId,
 *       fetchAuth, fetchSource, fetchTarget, fetchRefs, computeDiff,
 *     }
 *     Jede *fetch*-Funktion ist optional; ist sie nicht gesetzt, wird die
 *     Phase trotzdem durchlaufen (no-op) damit die UI konsistent bleibt.
 */

const PHASE_DEFS = [
  { id: "auth", label: "Authentifizierung" },
  { id: "source", label: "Quelle laden" },
  { id: "target", label: "Ziel laden" },
  { id: "refs", label: "Referenzen" },
  { id: "diff", label: "Diff berechnen" },
];

function initialPhases() {
  return PHASE_DEFS.map((p) => ({
    id: p.id,
    label: p.label,
    status: "waiting",
    detail: "",
    duration: null,
    error: null,
  }));
}

export function useCompareStream() {
  const [phases, setPhases] = useState(initialPhases);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setPhases(initialPhases());
    setRunning(false);
    setError(null);
  }, []);

  const setPhase = useCallback((id, patch) => {
    setPhases((prev) => prev.map((ph) => (ph.id === id ? { ...ph, ...patch } : ph)));
  }, []);

  const startCompare = useCallback(
    async (api, params = {}) => {
      if (typeof api !== "function") {
        throw new Error("useCompareStream.startCompare benötigt eine api()-Funktion");
      }
      const handlers = {
        auth: params.fetchAuth,
        source: params.fetchSource,
        target: params.fetchTarget,
        refs: params.fetchRefs,
        diff: params.computeDiff,
      };

      setRunning(true);
      setError(null);
      setPhases(initialPhases());
      const collected = {};

      try {
        for (const def of PHASE_DEFS) {
          const id = def.id;
          const fn = handlers[id];
          const t0 = Date.now();
          setPhase(id, { status: "active", detail: "" });
          try {
            const out = typeof fn === "function" ? await fn(api, params, collected) : null;
            collected[id] = out;
            const dur = `${Math.round((Date.now() - t0) / 100) / 10}s`;
            setPhase(id, { status: "done", duration: dur });
          } catch (phaseErr) {
            const dur = `${Math.round((Date.now() - t0) / 100) / 10}s`;
            setPhase(id, {
              status: "error",
              duration: dur,
              error: phaseErr?.message || String(phaseErr),
              detail: phaseErr?.message || "Fehler",
            });
            throw phaseErr;
          }
        }
        return collected;
      } catch (e) {
        setError(e?.message || String(e));
        throw e;
      } finally {
        setRunning(false);
      }
    },
    [setPhase]
  );

  return { phases, running, error, startCompare, setPhase, reset };
}

export default useCompareStream;
